/* eslint-disable */
// features/cx/outstanding/api.ts

import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  authApiClient,
  communicationApiClient,
  cxApiClient,
  onboardingApiClient,
} from "@shared/api/clients/apiClientFactory";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";
import {
  CreatePaymentAgreementPayload,
  OutstandingBill,
  OutstandingBillsFilters,
  OutstandingSummary,
  PaymentAgreement,
  PaymentAgreementFilters,
} from "./types";

// API Functions
export const outstandingApi = {
  // Outstanding Bills List
  getOutstandingBills: async (
    filters: OutstandingBillsFilters,
  ): Promise<{
    pageNumber: number;
    count: number;
    next: string | null;
    previous: string | null;
    result: OutstandingBill[];
  }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "outstanding-bills",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.searchData) query.push("search_data", filters.searchData);
        if (filters.isPagination) query.push("is_pagination", "1");
        if (filters.remoteConsumerNumber)
          query.push("remote_consumer_number", filters.remoteConsumerNumber);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  exportOutstandingBills: async (
    filters: OutstandingBillsFilters,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "outstanding-bills",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        if (filters.searchData) query.push("search_data", filters.searchData);
        if (filters.isPagination) query.push("is_pagination", "1");
        query.push("export_format", "csv");
        return query;
      },
    );

    const response = await cxApiClient.get(url);

    return response.data;
  },

  // Outstanding Summary
  getOutstandingSummary: async (
    remoteUtilityId: number,
  ): Promise<OutstandingSummary> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "outstanding/summary",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId.toString());
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<OutstandingSummary>(response.data.result);
  },

  // Create Payment Agreement
  createPaymentAgreement: async (payload: any): Promise<PaymentAgreement> => {
    const url = ApiEndpoints.createUrl("cx", "payment-agreement");
    const response = await cxApiClient.post(url, payload);
    return transformJsonToObject<PaymentAgreement>(response.data);
  },

  // Get Payment Agreement Detail
  getPaymentAgreementDetail: async (params: {
    remoteUtilityId: number;
    remoteBillId: number;
  }) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payment-agreement/detail",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        query.push("remote_bill_id", params.remoteBillId.toString());
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data?.result;
  },

  // Get Payment Agreements List (additional endpoint for listing all agreements)
  getPaymentAgreements: async (
    filters: PaymentAgreementFilters,
  ): Promise<{
    pageNumber: number;
    count: number;
    next: string | null;
    previous: string | null;
    result: PaymentAgreement[];
  }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payment-agreement",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.remoteBillId)
          query.push("remote_bill_id", filters.remoteBillId.toString());
        if (filters.consumerId)
          query.push("consumer_id", filters.consumerId.toString());
        if (filters.status) query.push("status", filters.status);
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return transformJsonToObject<{
      pageNumber: number;
      count: number;
      next: string | null;
      previous: string | null;
      result: PaymentAgreement[];
    }>(response.data);
  },

  // Get Paginated Payment Agreements List
  getPaymentAgreementList: async (
    filters: PaymentAgreementFilters,
  ): Promise<{
    pageNumber: number;
    count: number;
    next: string | null;
    previous: string | null;
    results: any[];
  }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "payment-agreement-list",
      (query) => {
        if (filters.remoteUtilityId)
          query.push("remote_utility_id", filters.remoteUtilityId.toString());
        if (filters.remoteBillId)
          query.push("remote_bill_id", filters.remoteBillId.toString());
        if (filters.consumerId)
          query.push("consumer_id", filters.consumerId.toString());
        if (filters.status) query.push("status", filters.status);
        if (filters.page) query.push("page", filters.page.toString());
        if (filters.limit) query.push("limit", filters.limit.toString());
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  // Update Payment Agreement (if needed)
  updatePaymentAgreement: async (
    agreementId: number,
    payload: Partial<CreatePaymentAgreementPayload>,
  ): Promise<PaymentAgreement> => {
    const url = ApiEndpoints.createUrlWithPathVariables(
      "cx",
      "payment-agreement",
      [agreementId.toString()],
    );
    const response = await cxApiClient.put(url, payload);
    return transformJsonToObject<PaymentAgreement>(response.data);
  },

  deletePaymentAgreement: async (params: {
    agreementId: number;
  }): Promise<{ result: any }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      `payment-agreement/${params.agreementId}`,
    );
    const response = await cxApiClient.put(url, {
      status: 3,
    });
    return transformJsonToObject<{ result: any }>(response.data);
  },

  getAccountDetails: async (params: {
    remoteUtilityId: number;
    consumerNumber: string;
  }) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "consumer-bill",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId.toString());
        query.push("consumer_number", params.consumerNumber);
        query.push("fetch_latest", "True");
        query.push("address_required", "True");
        query.push("is_category_required", "True");
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data?.result;
  },

  sendReminder: async (payload: any): Promise<{ result: any }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "direct-message",
    );

    const response = await communicationApiClient.post(url, payload);
    return transformJsonToObject<{ result: any }>(response.data);
  },

  updateResponseTime: async (payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrl("cx", "update-response-time");
    const response = await cxApiClient.post(url, payload);
    return response.data;
  },

  // Get Payment Agreement Detail
  getInstallmentFrequency: async (remoteUtilityId: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "choices/installment_frequency",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data?.result;
  },

  getWeekdayOptions: async (remoteUtilityId: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "choices/weekday",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data?.result;
  },

  getPreferredDayOfMonth: async (remoteUtilityId: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "choices/preferred_day_of_month",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data?.result;
  },

  getChannelChoices: async (params: {
    remoteUtilityId: string;
    context: string;
  }): Promise<Array<{ key: any; value: string }>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "choices/channel_choice",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId);
        query.push("context", params.context);
        return query;
      },
    );

    const response = await communicationApiClient.get(url);
    return response.data?.result || [];
  },

  getTemplates: async (params: {
    remoteUtilityId: string;
    channelType: string;
    isActive?: string;
  }): Promise<Array<any>> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "communication",
      "get-templates",
      (query) => {
        query.push("remote_utility_id", params.remoteUtilityId);
        query.push("channel_type", params.channelType);
        if (params.isActive !== undefined) {
          query.push("is_active", params.isActive);
        }
        return query;
      },
    );

    const response = await communicationApiClient.get(url);
    return response.data?.result || [];
  },

  getBulkCommunicationStep1: async (params: {
    remoteUtilityId: string | number;
    risk?: string;
    agingBucket?: string;
    status?: string;
    category?: string;
    subCategory?: string;
    premise?: string;
    page?: number;
    limit?: number;
    searchData?: string;
    consumerIds?: (string | number)[];
  }): Promise<{
    pageNumber: number;
    count: number;
    next: string | null;
    previous: string | null;
    results: any[];
  }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "bulk-communication/step-1",
      (query) => {
        if (params.remoteUtilityId)
          query.push("remote_utility_id", params.remoteUtilityId.toString());
        if (params.risk) query.push("risk", params.risk.toLowerCase());
        if (params.agingBucket)
          query.push("aging_bucket", params.agingBucket.toString());
        if (params.status) query.push("status", params.status);
        if (params.category) query.push("category", params.category);
        if (params.subCategory) query.push("sub_category", params.subCategory);
        if (params.premise) query.push("premise", params.premise);
        if (params.page) query.push("page", params.page.toString());
        if (params.limit) query.push("limit", params.limit.toString());
        if (params.searchData) query.push("search_data", params.searchData);
        if (params.consumerIds && params.consumerIds.length > 0) {
          params.consumerIds.forEach((id) => {
            query.push("consumer_id", id.toString());
          });
        }
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  saveBulkCommunicationStep1: async (payload: {
    consumerIds: number[];
    remoteUtilityId: number | string;
    is_select_all?: boolean;
  }) => {
    const url = ApiEndpoints.createUrl("cx", "bulk-communication/step-1");
    await cxApiClient.post(url, {
      consumer_ids: payload.consumerIds,
      remote_utility_id: Number(payload.remoteUtilityId),
      select_all: payload.is_select_all || false,
    });
  },

  saveBulkCommunicationStep3: async (formData: FormData): Promise<any> => {
    const url = ApiEndpoints.createUrl("cx", "bulk-communication/step-3");
    const response = await cxApiClient.post(url, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  },

  getBulkCommunicationAuditSummary: async (
    remoteUtilityId: string | number,
  ): Promise<{
    totalBatches: number;
    bulkNotices: number;
    bulkReminders: number;
    totalDelivered: number;
    totalFailed: number;
  }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "bulk-communication/audit-summary",
      (query) => {
        if (remoteUtilityId) {
          query.push("remote_utility_id", remoteUtilityId.toString());
        }
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data?.result !== undefined
      ? response.data.result
      : response.data;
  },

  getBulkCommunicationAuditBatches: async (params: {
    remoteUtilityId: string | number;
    page?: number;
    limit?: number;
    searchData?: string;
    type?: string;
    template?: string;
    status?: string;
  }): Promise<{
    pageNumber: number;
    count: number;
    next: string | null;
    previous: string | null;
    results: any[];
    result: any[];
  }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      "bulk-communication/audit-batches",
      (query) => {
        if (params.remoteUtilityId)
          query.push("remote_utility_id", params.remoteUtilityId.toString());
        if (params.page) query.push("page", params.page.toString());
        if (params.limit) query.push("limit", params.limit.toString());
        if (params.searchData) query.push("search_data", params.searchData);
        if (params.type) query.push("type", params.type);
        if (params.template) query.push("template", params.template);
        if (params.status) query.push("status", params.status);
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  getUserNames: async (params: {
    remoteUtilityId: string | number;
    page?: number;
    limit?: number;
  }): Promise<{
    pageNumber: number;
    count: number;
    next: string | null;
    previous: string | null;
    result: any[];
  }> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "user-name",
      (query) => {
        if (params.remoteUtilityId)
          query.push("remote_utility_id", params.remoteUtilityId.toString());
        if (params.page) query.push("page", params.page.toString());
        if (params.limit) query.push("limit", params.limit.toString());
        return query;
      },
    );

    const response = await authApiClient.get(url);
    return response.data;
  },

  getBulkCommunicationResult: async (params: {
    id: string | number;
    remoteUtilityId: string | number;
    page?: number;
    limit?: number;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "cx",
      `bulk-communication/${params.id}/result`,
      (query) => {
        if (params.remoteUtilityId) {
          query.push("remote_utility_id", params.remoteUtilityId.toString());
        }
        if (params.page) {
          query.push("page", params.page.toString());
        }
        if (params.limit) {
          query.push("limit", params.limit.toString());
        }
        return query;
      },
    );

    const response = await cxApiClient.get(url);
    return response.data;
  },

  getCategory: async (utilityId: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/category_json",
      (query) => {
        query.push("remote_utility_id", utilityId);
        query.push("config_level", "category");
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data.result ?? [];
  },

  getSubCategory: async (utilityId: string, code_list: any[]) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/category_json",
      (query) => {
        query.push("remote_utility_id", utilityId);
        query.push("config_level", "sub_category");
        for (let code of code_list) {
          query.push("code_list", code);
        }
      },
    );

    const response = await onboardingApiClient.get(url);
    return response.data.result ?? [];
  },

  getPremise: async (params: {
    remote_utility_id: string;
    config_level: string;
  }): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/territory_json",
      (qs) => {
        qs.push("remote_utility_id", params.remote_utility_id);
        qs.push("config_level", params.config_level);
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject(response.data);
  },

  downloadBulkCommunicationReport: async (
    batchId: string | number,
    remoteUtilityId: string | number,
  ): Promise<Blob> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "bulk-communication",
      [batchId, "report"],
      (query) => {
        if (remoteUtilityId) {
          query.push("remote_utility_id", remoteUtilityId.toString());
        }
        return query;
      },
    );

    const response = await cxApiClient.get(url, {
      responseType: "blob",
    });
    return response.data;
  },

  downloadBulkCommunicationErrors: async (
    batchId: string | number,
    remoteUtilityId: string | number,
  ): Promise<Blob> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "cx",
      "bulk-communication",
      [batchId, "errors"],
      (query) => {
        if (remoteUtilityId) {
          query.push("remote_utility_id", remoteUtilityId.toString());
        }
        return query;
      },
    );

    const response = await cxApiClient.get(url, {
      responseType: "blob",
    });
    return response.data;
  },
};
